import { RefundModel } from 'api-hooks/refund/refund';
import React from 'react';

import { SaleInvoiceRefundType } from '../components/form';
import { FormType } from '../components/form-type';

interface Props {
  refund?: RefundModel;
  saleInvoice?: SaleInvoiceRefundType;
}

export default function useGetDefaultValues(props: Props) {
  const { refund, saleInvoice } = props;

  const defaultValues = React.useMemo<FormType>(
    () => ({
      number: refund?.number ?? '',
      requestedAt: refund?.requestedAt ?? new Date(),
      isHideAgent: !!refund?.releasePurchase,
      reason: refund?.reason ?? '',
      activitySales: [],
      cruiseSales: [],
      documentSales: [],
      flightSales: [],
      hotelSales: [],
      productSales: [],
      tourSales: [],
      flightPurchases: [],
      hotelPurchases: [],
      activityPurchases: [],
      cruisePurchases: [],
      documentPurchases: [],
      releasePurchase: refund?.releasePurchase
        ? {
            agentId: refund.releasePurchase.agent.id,
            amount: refund.releasePurchase.amount,
            paymentableId: refund.releasePurchase.paymentable.chequeGiro?.id
              ? refund.releasePurchase.paymentable.chequeGiro?.id
              : refund.releasePurchase.paymentable.id,
            paymentMethodId: refund.releasePurchase.paymentMethod.id,
            refId: refund.releasePurchase.purchase.refId ?? '',
            id: {
              id: refund.releasePurchase.purchase.id,
              label: refund.releasePurchase.purchase.invoiceNumber ?? '',
            },
            chequeNote: {
              availableAt: refund.releasePurchase.paymentable.availableAt,
              bankAccountId: refund.releasePurchase.paymentable.bankAccount?.id,
              chequeGiroId: refund.releasePurchase.paymentable.chequeGiro?.id,
              expiredAt: refund.releasePurchase.paymentable.expiredAt,
              issuedAt: refund.releasePurchase.paymentable.issuedAt,
              referenceNumber:
                refund.releasePurchase.paymentable.referenceNumber,
            },
            //Information Additional
            transactionAt: refund.releasePurchase.purchase.transactionAt,
            total: refund.releasePurchase.purchase.total,
            agent: refund.releasePurchase.purchase.agent.name,
            paymentChannel:
              refund.releasePurchase.purchase.paymentChannel?.name ?? '',
          }
        : {
            id: null,
            agentId: null,
            amount: 0,
            chequeNote: null,
            paymentableId: null,
            paymentMethodId: null,
            refId: null,

            //Information Additional
            agent: '',
            paymentChannel: '',
            total: 0,
            transactionAt: null,
          },
      releaseSale: refund?.releaseSale
        ? {
            amount: refund.releaseSale.amount,
            customerBillingId: refund?.releaseSale?.customerBilling?.id ?? null,
            customerId: refund.releaseSale.customer.id,
            paymentableId: refund.releaseSale.paymentable.chequeGiro?.id
              ? refund.releaseSale.paymentable.chequeGiro?.id
              : refund.releaseSale.paymentable.id,
            paymentMethodId: refund.releaseSale.paymentMethod.id,
            refId: refund.releaseSale.sale.refId,
            id: {
              id: refund.releaseSale.sale.id,
              label: refund.releaseSale.sale.invoiceNumber,
            },
            chequeNote: {
              availableAt: refund.releaseSale.paymentable.availableAt,
              bankAccountId: refund.releaseSale.paymentable.bankAccount?.id,
              chequeGiroId: refund.releaseSale.paymentable.chequeGiro?.id,
              expiredAt: refund.releaseSale.paymentable.expiredAt,
              issuedAt: refund.releaseSale.paymentable.issuedAt,
              referenceNumber: refund.releaseSale.paymentable.referenceNumber,
            },
            //Information Additional
            adminId: refund.releaseSale.sale.admin.id,
            operatorId: refund.releaseSale.sale.operator?.id,
            salesmanId: refund.releaseSale.sale.salesman?.id,
            code: refund.releaseSale.sale.code || '',
            description: refund.releaseSale.sale.description,
            transactionAt: refund.releaseSale.sale.transactionAt,
            total: refund.releaseSale.sale.total,

            previousSale: {
              customerBillingId:
                refund?.releaseSale?.customerBilling?.id ?? null,
              customerId: refund.releaseSale.customer.id,
              adminId: refund.releaseSale.sale.admin.id,
              operatorId: refund.releaseSale.sale.operator?.id,
              salesmanId: refund.releaseSale.sale.salesman?.id,
              code: refund.releaseSale.sale.code || '',
              description: refund.releaseSale.sale.description,
              transactionAt: refund.releaseSale.sale.transactionAt,
              total: refund.releaseSale.sale.total,
            },
          }
        : {
            id: saleInvoice?.id ?? null,
            amount: 0,
            chequeNote: null,
            customerBillingId: saleInvoice?.customerBillingId || null,
            customerId: saleInvoice?.customerId || null,
            paymentableId: null,
            paymentMethodId: null,
            refId: saleInvoice?.refId || null,

            //Information Additional
            adminId: saleInvoice?.adminId || null,
            code: saleInvoice?.code || '',
            description: saleInvoice?.description || '',
            operatorId: saleInvoice?.operatorId || null,
            salesmanId: saleInvoice?.salesmanId || null,
            total: saleInvoice?.total || 0,
            transactionAt: saleInvoice?.transactionAt || null,

            previousSale: {
              customerBillingId: saleInvoice?.customerBillingId || null,
              customerId: saleInvoice?.customerId || null,
              adminId: saleInvoice?.adminId || null,
              code: saleInvoice?.code || '',
              description: saleInvoice?.description || '',
              operatorId: saleInvoice?.operatorId || null,
              salesmanId: saleInvoice?.salesmanId || null,
              total: saleInvoice?.total || 0,
              transactionAt: saleInvoice?.transactionAt || null,
            },
          },
      transactionOrderType:
        refund?.releasePurchase?.purchase?.transactionType ?? '',
      transactionSaleType:
        saleInvoice?.transactionSaleType ??
        refund?.releaseSale.sale.transactionType ??
        '',
    }),
    [refund, saleInvoice],
  );

  return defaultValues;
}
